import React, { useCallback, useEffect, useState } from 'react';
import { Check, Clipboard, Loader2, Plus, ShieldCheck, X } from 'lucide-react';
import {
  createInvitation,
  getInvitations,
  revokeInvitation,
} from '../services/api';
import { CreatedInvitation, Invitation, InvitationStatus } from '../types';

const statusLabels: Record<InvitationStatus, string> = {
  unused: '未使用',
  used: '已使用',
  expired: '已过期',
  revoked: '已撤销',
};

const statusClasses: Record<InvitationStatus, string> = {
  unused: 'bg-green-50 text-green-700',
  used: 'bg-blue-50 text-blue-700',
  expired: 'bg-gray-100 text-gray-500',
  revoked: 'bg-red-50 text-red-600',
};

const formatDate = (timestamp?: number | null) => {
  if (!timestamp) return '—';
  return new Date(timestamp * 1000).toLocaleString('zh-CN', {
    hour12: false,
  });
};

const InvitationManager: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [createdInvitation, setCreatedInvitation] = useState<CreatedInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const loadInvitations = useCallback(async () => {
    try {
      setInvitations(await getInvitations());
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '邀请码加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInvitations();
  }, [loadInvitations]);

  const handleCreate = async () => {
    setCreating(true);
    setCopied(false);
    setError('');
    try {
      const invitation = await createInvitation();
      setCreatedInvitation(invitation);
      await loadInvitations();
    } catch (err) {
      setError(err instanceof Error ? err.message : '邀请码生成失败');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdInvitation) return;
    try {
      await navigator.clipboard.writeText(createdInvitation.code);
      setCopied(true);
    } catch {
      setError('复制失败，请手动选择邀请码复制');
    }
  };

  const handleRevoke = async (invitationId: number) => {
    setRevokingId(invitationId);
    setError('');
    try {
      await revokeInvitation(invitationId);
      await loadInvitations();
    } catch (err) {
      setError(err instanceof Error ? err.message : '邀请码撤销失败');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-extrabold text-gray-800 flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-yellow-100 text-yellow-700">
              <ShieldCheck className="w-4 h-4" />
            </span>
            注册邀请码
          </h3>
          <p className="text-xs text-gray-500 mt-2">
            每个邀请码只能注册一次，生成后 7 天失效。
          </p>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating}
          className="ios-btn-primary px-5 py-3 rounded-xl flex items-center gap-2 disabled:opacity-60"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          生成邀请码
        </button>
      </div>

      {createdInvitation && (
        <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-5">
          <div className="text-sm font-bold text-gray-900">请立即保存这个邀请码</div>
          <p className="text-xs text-gray-500 mt-1">完整邀请码只在本次生成后显示。</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <code className="flex-1 rounded-xl bg-white border border-yellow-200 px-4 py-3 break-all text-sm">
              {createdInvitation.code}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              aria-label="复制邀请码"
              className="rounded-xl bg-gray-900 text-white px-4 py-3 flex items-center justify-center gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Clipboard className="w-4 h-4" />}
              {copied ? '已复制' : '复制'}
            </button>
          </div>
        </div>
      )}

      {error && <div className="rounded-xl bg-red-50 p-3 text-sm font-bold text-red-600">{error}</div>}

      <div className="ios-card rounded-[2rem] bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : invitations.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">还没有生成过邀请码</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-5 py-3">邀请码</th>
                  <th className="text-left px-5 py-3">状态</th>
                  <th className="text-left px-5 py-3">创建时间</th>
                  <th className="text-left px-5 py-3">失效时间</th>
                  <th className="text-left px-5 py-3">使用账号</th>
                  <th className="text-right px-5 py-3">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invitations.map((invitation) => (
                  <tr key={invitation.id}>
                    <td className="px-5 py-4 font-mono">{invitation.code_prefix}…</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${statusClasses[invitation.status]}`}>
                        {statusLabels[invitation.status]}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500 whitespace-nowrap">{formatDate(invitation.created_at)}</td>
                    <td className="px-5 py-4 text-gray-500 whitespace-nowrap">{formatDate(invitation.expires_at)}</td>
                    <td className="px-5 py-4 text-gray-500">{invitation.used_by_username || '—'}</td>
                    <td className="px-5 py-4 text-right">
                      {invitation.status === 'unused' && (
                        <button
                          type="button"
                          aria-label={`撤销邀请码 ${invitation.code_prefix}`}
                          onClick={() => handleRevoke(invitation.id)}
                          disabled={revokingId === invitation.id}
                          className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 font-bold disabled:opacity-50"
                        >
                          {revokingId === invitation.id
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <X className="w-4 h-4" />}
                          撤销
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default InvitationManager;
